import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_HOTELAPI_BASE_URL;

// ─────────────────────────────────────────────────────────────────────────────
// SCENARIO SWITCH
// true  → use hardcoded mock data (current, no endpoint yet)
// false → call the real API endpoint
// ─────────────────────────────────────────────────────────────────────────────
// const USE_MOCK = false;

/**
 * Helper to map and parse raw static data fields
 */
const mapHotelStaticData = (hotel) => {
  if (!hotel) return null;

  let formattedAddress = hotel.address;
  try {
    const parsedAddress =
      typeof hotel.address === "string"
        ? JSON.parse(hotel.address)
        : hotel.address;
    if (parsedAddress) {
      if (parsedAddress.Adr) {
        formattedAddress = `${parsedAddress.Adr}${parsedAddress.Adr2 ? ", " + parsedAddress.Adr2 : ""}, ${parsedAddress.City?.Name || ""}, ${parsedAddress.PostalCode || ""}, ${parsedAddress.Country?.Name || ""}`;
      } else if (parsedAddress.fulladdr) {
        formattedAddress = parsedAddress.fulladdr;
      } else if (
        typeof parsedAddress === "object" &&
        Object.keys(parsedAddress).length > 0
      ) {
        // Generic fallback to prevent object rendering
        formattedAddress =
          parsedAddress.line1 || parsedAddress.city || "Unknown Address";
      }
    }
  } catch (e) {
    console.warn("Address parse failed for hotel:", hotel.name);
  }

  let imageUrl = "/assets/images/hotel/hotel.jpg";
  let allImages = [];
  try {
    const images =
      typeof hotel.images === "string"
        ? JSON.parse(hotel.images)
        : hotel.images;
    if (Array.isArray(images) && images.length > 0) {
      imageUrl =
        images.find((img) => img.Sz === "XXL")?.Url ||
        images[0]?.Url ||
        imageUrl;
      allImages = images.map((img) => img.Url).filter(Boolean);
    }
  } catch (e) {
    console.warn("Images parse failed for hotel:", hotel.name);
  }

  let amenityList = [];
  try {
    const facilities =
      typeof hotel.facilities === "string"
        ? JSON.parse(hotel.facilities)
        : hotel.facilities;
    if (Array.isArray(facilities)) {
      amenityList = facilities.map((f) => f.Name || f);
    }
  } catch (e) {
    console.warn("Facilities parse failed for hotel:", hotel.name);
  }

  let parsedDescription = {};
  try {
    parsedDescription =
      typeof hotel.description === "string"
        ? JSON.parse(hotel.description)
        : hotel.description || {};
  } catch (e) {
    parsedDescription = { Overview: hotel.description };
  }

  // Ensure contact info is also parsed if present
  let parsedContact = {};
  try {
    parsedContact =
      typeof hotel.contact === "string"
        ? JSON.parse(hotel.contact)
        : hotel.contact;
  } catch (e) {}

  let parsedGeo = {};
  try {
    const geo = hotel.geoLocation || hotel.geolocation;
    if (geo) {
      parsedGeo = typeof geo === "string" ? JSON.parse(geo) : geo;
    }
  } catch (e) {
    console.warn("GeoLocation parse failed for hotel:", hotel.name);
  }

  const latitude =
    hotel.latitude ||
    hotel.lat ||
    parsedGeo.Lt ||
    parsedGeo.latitude ||
    hotel.position?.latitude;
  const longitude =
    hotel.longitude ||
    hotel.lng ||
    parsedGeo.Ln ||
    parsedGeo.longitude ||
    hotel.position?.longitude;

  return {
    hotelid: hotel.hotelid,
    hotelName: hotel.name,
    starRating: parseInt(hotel.rating) || 0,
    address: formattedAddress,
    city: hotel.city,
    image: imageUrl,
    allImages: allImages.length > 0 ? allImages : [imageUrl],
    amenities: amenityList,
    description: parsedDescription,
    contact: parsedContact,
    propertyType: hotel.propertytype,
    latitude: latitude ? parseFloat(latitude) : null,
    longitude: longitude ? parseFloat(longitude) : null,
  };
};

/**
 * Fetch static data for a batch of hotel IDs
 * @param {string[]} hotelIds
 */
export const getHotelStaticData = async (hotelIds) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/StaticContent/details`,
      hotelIds,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );

    if (Array.isArray(response.data)) {
      return response.data.map(mapHotelStaticData).filter(Boolean);
    }
    return response.data;
  } catch (error) {
    console.error("Static Data API failed:", error);
    return [];
  }
};

/**
 * Fetch search results (pricing/availability) for a batch of hotel IDs
 * @param {object} payload
 */
export const getHotelSearch = async (payload) => {
  try {
    // const startTime = performance.now();
    // console.log(`[HotelSearch API] Request started at: ${new Date().toLocaleTimeString()}`);

    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/HotelSearch`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );

    // const endTime = performance.now();
    // const timeTakenInSeconds = ((endTime - startTime) / 1000).toFixed(2);
    // console.log(`[HotelSearch API] Response received at: ${new Date().toLocaleTimeString()}`);
    // console.log(`[HotelSearch API] Total time taken: ${timeTakenInSeconds} seconds`);

    return response.data;
  } catch (error) {
    console.error("Hotel Search API failed:", error);
    return [];
  }
};

/**
 * Fetch room details for a specific hotel
 * @param {object} payload - Contains traceId, checkInDate, checkOutDate, currency, HotelId, roomGuests, nationality, Key
 */
export const getHotelRoomDetails = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/HotelRooms`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Room Details API failed:", error);
    throw error;
  }
};

/**
 * Fetch final pricing for a specific room plan
 * @param {object} payload - Contains corporateTraveler, corporateCode, travelerType, traceId, roomPlan, roomsId, supplierParameter, key
 */
export const getHotelPricing = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/HotelPricing`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Pricing API failed:", error);
    throw error;
  }
};
export const bookHotel = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/HotelBook`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Booking API failed:", error);
    throw error;
  }
};

/**
 * Hold hotel booking specifically for the approval workflow
 */
export const holdHotelForApproval = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/HotelHold`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Approval Hold API failed:", error);
    throw error;
  }
};

/**
 * Retrieve booking details
 * @param {object} payload - Contains traceId, confirmationNo, key, supplierParameter
 */
export const retrieveHotelBooking = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/HotelRetrieveBooking`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Retrieve Booking API failed:", error);
    throw error;
  }
};
/**
 * Retrieve booking details by ID
 * @param {string} bookingRefId
 */
export const getBookingDetailById = async (bookingRefId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/Hotel/GetBookingDetailById?bookingRefId=${bookingRefId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Get Booking Detail By Id API failed:", error);
    throw error;
  }
};
/**
 * Retrieve all hotel bookings
 */
export const getAllHotelBookings = async (userId, bookingStatus, paymentStatus, fromDate, toDate, hotelPNR, bookingRefNo, hotelName) => {
  try {
    const params = { userId };
    if (bookingStatus) {
      params.bookingStatus = bookingStatus;
    }
    if (paymentStatus) {
      params.paymentStatus = paymentStatus;
    }
    if (fromDate) {
      params.fromDate = fromDate;
    }
    if (toDate) {
      params.toDate = toDate;
    }
    if (hotelPNR) {
      params.hotelPNR = hotelPNR;
    }
    if (bookingRefNo) {
      params.bookingRefNo = bookingRefNo;
    }
    if (hotelName) {
      params.hotelName = hotelName;
    }
    const response = await axios.get(
      `${API_BASE_URL}/api/Hotel/GetAllHotelBookings`,
      {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Get All Hotel Bookings API failed:", error);
    throw error;
  }
};
export const hotelApproval = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/HotelApproval`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Approval API failed:", error);
    throw error;
  }
};

/**
 * Cancel a hotel booking
 * @param {Object} payload
 */
export const hotelCancel = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/HotelCancel`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Cancel API failed:", error);
    throw error;
  }
};

/**
 * Hold a hotel booking
 * @param {Object} payload
 */
export const holdHotel = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/HotelHold`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Hold API failed:", error);
    throw error;
  }
};

/**
 * Send hotel booking notifications (Email/SMS/WhatsApp)
 * @param {Object} payload
 */
export const SendHotelBookingNotifications = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Utility/SendHotelBookingNotifications`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Send Hotel Notifications API failed:", error);
    throw error;
  }
};

/**
 * Perform Hotel Book Hold action
 * @param {Object} payload - traceId, supplierParameter, key, confirmationNo, bookingRefId
 */
export const hotelBookHold = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/HotelBookHold`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Book Hold API failed:", error);
    throw error;
  }
};

/**
 * Approve a hotel booking (after collecting payment)
 * @param {Object} payload - bookingRefId, supplierParameter, traceId
 */
export const hotelBookApproval = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/HotelBookApproval`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Book Approval API failed:", error);
    throw error;
  }
};

/**
 * Save Low Fare Hotel Log
 * @param {Object} payload 
 */
export const saveLowFareHotels = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/SaveLowFareHotels`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Save Low Fare Hotels API failed:", error);
    throw error;
  }
};

/**
 * Save Hotel Details
 * @param {Object} payload 
 */
export const SaveHotelDetails = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/Hotel/SaveHotelDetails`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Save Hotel Details API failed:", error);
    throw error;
  }
};

/**
 * Fetch hotel cancellation reports
 * @param {string} userId
 */
export const getHotelCancellationReport = async (userId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/Hotel/CancellationReport`,
      {
        params: { userId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Hotel Get Cancellation Report API failed:", error);
    throw error;
  }
};

